import type { IconType } from 'react-icons';
import {
  FiDollarSign,
  FiCalendar,
  FiMessageCircle,
  FiThermometer,
  FiFileText,
  FiCamera,
  FiFolder,
  FiMapPin,
  FiMonitor,
  FiSmartphone,
  FiBookOpen,
} from 'react-icons/fi';

export type PricingFeature = {
  id: string;
  text: string;
  icon: IconType;
};

const FEATURES: Omit<PricingFeature, 'icon'>[] = [
  { id: 'expense', text: 'Expense tracking & splitting' },
  { id: 'calendar', text: 'Custody calendar with exceptions' },
  { id: 'messaging', text: 'Secure, documented messaging' },
  { id: 'journal', text: 'Private journal, not shared' },
  { id: 'temp-check', text: 'Tone check for messages' },
  { id: 'exports', text: 'Court-ready records & exports' },
  { id: 'receipt-scanner', text: 'Automatically scan expense receipts' },
  { id: 'vault', text: 'Unlimited file vault storage' },
  { id: 'gps', text: 'GPS location check-ins' },
  { id: 'apps', text: 'iOS, Android & web apps' },
];

const ICONS_MAIN = {
  expense: FiDollarSign,
  calendar: FiCalendar,
  messaging: FiMessageCircle,
  journal: FiBookOpen,
  'temp-check': FiThermometer,
  exports: FiFileText,
  'receipt-scanner': FiCamera,
  vault: FiFolder,
  gps: FiMapPin,
  apps: FiMonitor,
} as const;

const ICONS_LEARN = {
  ...ICONS_MAIN,
  apps: FiSmartphone,
} as const;

export type PricingFeaturesVariant = 'main' | 'learn';

/**
 * Single source of truth for the pricing card feature list.
 * - main: full list including GPS, uses Monitor icon for apps (website pricing)
 * - learn: no GPS, uses Smartphone icon for apps (learn/coparents, learn/practitioners)
 */
export function getPricingFeatures(variant: PricingFeaturesVariant): PricingFeature[] {
  const icons = variant === 'main' ? ICONS_MAIN : ICONS_LEARN;
  const excludeIds = variant === 'learn' ? ['gps'] : [];
  return FEATURES.filter((f) => !excludeIds.includes(f.id)).map((feature) => ({
    ...feature,
    icon: icons[feature.id as keyof typeof icons],
  }));
}
